import React from "react";
import { ArrowRight, Coins } from "lucide-react";

interface CashbackEntryGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when the admin confirms the navigation to the completed orders list. */
  onConfirm: () => void;
}

/**
 * Explainer modal for the Cashback Issuances tab.
 *
 * Cashback is order-scoped: one credit per completed order. Rather than
 * duplicating the orders list with a buyer/order picker, this modal tells
 * the admin exactly how the flow works and hands them off to the orders
 * page (filtered to completed orders) where the row-level action lives.
 */
const CashbackEntryGuideModal: React.FC<CashbackEntryGuideModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cashback-guide-title"
        className="flex max-h-[min(92dvh,700px)] w-full flex-col overflow-y-auto rounded-t-2xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-lg outline-none sm:max-h-[min(90vh,700px)] sm:max-w-md sm:rounded-2xl sm:p-6 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]"
      >
        <div className="flex justify-center mb-4">
          <div className="bg-lightPurple text-accent rounded-full p-4">
            <Coins size={28} />
          </div>
        </div>
        <h2
          id="cashback-guide-title"
          className="text-center text-xl font-semibold mb-2"
        >
          How to issue cashback
        </h2>
        <p className="text-center text-gray-600 mb-5 text-sm">
          Cashback is tied to a single completed order, and each order can
          only receive one cashback.
        </p>

        <ol className="space-y-3 mb-6 min-w-0">
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-semibold flex items-center justify-center mt-0.5">
              1
            </span>
            <div className="text-sm text-gray-700">
              Open the <span className="font-semibold">Completed Orders</span>{" "}
              list.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-semibold flex items-center justify-center mt-0.5">
              2
            </span>
            <div className="text-sm text-gray-700">
              Find the order, then either:
              <ul className="mt-1 ml-4 list-disc text-gray-600 text-sm space-y-0.5">
                <li>
                  Open its <span className="font-semibold">actions menu</span>{" "}
                  (the three-dot icon on the row), or
                </li>
                <li>
                  Open the <span className="font-semibold">order details</span>{" "}
                  page.
                </li>
              </ul>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-semibold flex items-center justify-center mt-0.5">
              3
            </span>
            <div className="text-sm text-gray-700">
              Click <span className="font-semibold">Issue Cashback</span>,
              enter the percentage, and confirm.
            </div>
          </li>
        </ol>

        <div className="mt-auto flex flex-col gap-3 sm:flex-row sm:justify-stretch">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-md bg-gray-100 py-2.5 px-4 text-gray-800 hover:bg-gray-200 sm:flex-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2.5 px-4 text-white hover:bg-primaryHover sm:flex-1"
          >
            Completed orders
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashbackEntryGuideModal;
